import type { Client } from "openapi-fetch";
import type { paths } from "@comandero/client";

/**
 * Unified, agnostic API client interface.
 */
export type ApiClient = Client<paths>;

export interface SignInOptions {
  email: string;
  password: string;
}

export interface SignUpOptions {
  name: string;
  email: string;
  password: string;
}

/**
 * Agnostic API wrapper for shared logic using OpenAPI client.
 */
export const sharedApi = {
  async signIn(api: ApiClient, options: SignInOptions) {
    return api.POST("/api/auth/sign-in/email", {
      body: {
        email: options.email,
        password: options.password,
      },
    });
  },

  async signUp(api: ApiClient, options: SignUpOptions) {
    return api.POST("/api/auth/sign-up/email", {
      body: {
        name: options.name,
        email: options.email,
        password: options.password,
      },
    });
  },

  /**
   * Revokes the active session on the server.
   *
   * `activeAccountId` is the userId of the account being signed out. It is sent
   * as an EXPLICIT `X-Active-Account` header instead of letting the API client
   * derive it from the local account registry at request time: `useExecuteSignOut`
   * drops the account from that registry while the revocation is in flight, so a
   * request that resolves the selector lazily can go out with no header at all
   * (the backend answers 401 and the session row is never deleted — the reported
   * "logout does not stick" race). It also prevents signing out the WRONG account
   * when a fallback account becomes active in the same tick.
   *
   * Optional for callers that legitimately have no account to name (e.g. the
   * pre-login cleanup in the sign-in flow); with no id the transport falls back
   * to the registry as before.
   */
  async signOut(api: ApiClient, activeAccountId?: string) {
    return api
      .POST("/api/auth/sign-out", {
        headers: activeAccountId ? { "X-Active-Account": activeAccountId } : undefined,
      })
      .catch(() => null);
  },

  async getSession(api: ApiClient) {
    return api.GET("/api/auth/get-session", {});
  },

  async listSessions(api: ApiClient) {
    return api.GET("/api/auth/list-sessions", {});
  },

  /**
   * Revokes a specific session (closes another device).
   *
   * P23/CC-60 · P25 (MEASURED, 2026-08-02): this used to return the raw
   * `openapi-fetch` result, which **NEVER throws**—a 500 arrives as `{ error }`
   * and the caller continues as if it succeeded. It was the first link that made
   * the UI say "done" without closing anything. It THROWS on a non-2xx so the
   * state-machine actor can exit through `onError`.
   */
  async revokeSession(api: ApiClient, sessionId: string) {
    const result = await api.POST("/api/auth/revoke-session", {
      body: { id: sessionId },
    });
    const { status } = result.response;
    if (result.error || !result.response.ok) {
      const err = new Error(`revoke-session failed with status ${status}`);
      Object.assign(err, { status, cause: result.error });
      throw err;
    }
    return result;
  },

  async forgotPassword(api: ApiClient, options: { email: string }) {
    return api.POST("/api/auth/forgot-password", {
      body: { email: options.email },
    });
  },

  /** Changes the password (requires the current one; closes other sessions). */
  async changePassword(
    api: ApiClient,
    options: { current_password: string; new_password: string },
  ) {
    return api.POST("/api/auth/change-password", {
      body: { current_password: options.current_password, new_password: options.new_password },
    });
  },

  /** Verifies the email with the token received by email. */
  async verifyEmail(api: ApiClient, options: { token: string }) {
    return api.POST("/api/auth/verify-email", {
      body: { token: options.token },
    });
  },

  /** Resends the verification email to the authenticated user. */
  async sendVerificationEmail(api: ApiClient) {
    return api.POST("/api/auth/send-verification-email", {});
  },

  /** Schedules account deletion (grace period; closes all sessions). */
  async requestAccountDeletion(api: ApiClient) {
    return api.POST("/api/auth/delete-account", {});
  },

  /** Cancels a pending account deletion (recovery). */
  async restoreAccount(api: ApiClient) {
    return api.POST("/api/auth/restore-account", {});
  },

  /** Account deletion status (request and purge dates, or null). */
  async getDeletionStatus(api: ApiClient) {
    return api.GET("/api/auth/deletion-status", {});
  },

  /** Social provider enablement status (Google/Apple). */
  async socialProviders(api: ApiClient) {
    return api.GET("/api/auth/social/providers", {});
  },

  /** Sign-in methods linked to the authenticated user. */
  async loginMethods(api: ApiClient) {
    return api.GET("/api/auth/login-methods", {});
  },

  /** Unlinks a social provider (with a last-method safeguard in the backend). */
  async unlinkAccount(api: ApiClient, provider: string) {
    return api.POST("/api/auth/unlink-account", { body: { provider } });
  },
};
